import classNames from 'classnames';
import {IShieldProps} from 'modules/shields/components/Shield';
import {Category} from 'models';
import {ICatalogMeta, IProductShort} from 'modules/products/types';
import React, {FC, useCallback, useEffect, useMemo, useRef} from 'react';
import style from './style.module.scss';
import {Modal} from 'modules/modals/components/Modal';
import {MODALS} from 'modules/modals/constants';
import {SkeletonWrapper} from 'modules/skeleton/components/SkeletonWrapper';
import dynamic from 'next/dynamic';
import {ModalLazyLoader} from 'components/ModalLazyLoader';
import {EmptyProductList} from 'modules/products/components/EmptyProductList';
import {ProductBannerCard} from '../ProductBannerCard';
import {BANNER_LIST_INDEX} from './constants';
import {ProductSnippet} from 'modules/products/components/ProductSnippet';
import {Button} from 'components/Button';
import {CATALOG_PAGE_SIZE} from 'modules/products/constants';
import {ListPagination} from 'plugins/modules/pagination/components/ListPagination';
import Icon from 'components/Icon';
import {useIntersectionObserver} from 'core/hooks/useIntersectionObserver';
import {getMediaSrc} from 'core/helpers/media';
import {useListingContext} from 'plugins/modules/listing/context';
import {PRODUCT_PLACEHOLDER} from 'core/constants';
import {useProductsPLPViewGtag} from 'hooks/analytics/gtag/catalog/useProductsPLPViewGtag';
import {isbot} from 'isbot';
import FlocktoryCategoryView from 'components/flocktory/FlocktoryCategoryView';
import {withRedirectParam} from 'modules/product-card/helpers/redirect';

const AvailabilityInfoModal = dynamic(
    () => import('modules/availability-delivery-pickups/components/AvailabilityInfoModal'),
    {
        loading: () => <ModalLazyLoader />,
    },
);

export interface IProps {
    isFilterPanelOpen?: boolean;
    shields?: IShieldProps[];
    categoryType?: Category['type'];
    className?: string;
}

// eslint-disable-next-line complexity,max-lines-per-function
export const CatalogProductList: FC<IProps> = ({isFilterPanelOpen, className}) => {
    const {
        isFetching,
        isLoading,
        list,
        handleLoadPrev,
        handleLoadNext,
        handleSetPage,
        hasMoreProductsOnTop,
        isLoadingNext,
        isLoadingPrev,
        meta,
        view = 'list',
        pageType,
        pagination,
    } = useListingContext<IProductShort>();

    const isSearchListing = 'SEARCH' === pageType;
    const isLazyLoadingDisabled = 'STOCK_X' === pageType;

    const sendProductsListingGtag = useProductsPLPViewGtag();
    const sentProductIdsRef = useRef<Set<string>>(new Set());

    useEffect(() => {
        if (!isLoading && 0 < list.length) {
            const newProducts = list.filter((product) => !sentProductIdsRef.current.has(String(product.offerId)));
            if (0 < newProducts.length) {
                const chunkSize = 6;
                for (let i = 0; i < newProducts.length; i += chunkSize) {
                    const chunk = newProducts.slice(i, i + chunkSize);
                    sendProductsListingGtag(chunk);
                    chunk.forEach((product) => sentProductIdsRef.current.add(String(product.offerId)));
                }
            }
        }
    }, [isLoading, list, sendProductsListingGtag]);

    useEffect(() => {
        const productScrollId = sessionStorage.getItem('productScrollId');
        sessionStorage.removeItem('productScrollId');

        const node = document.querySelector(`[data-product-card-id="${productScrollId}"]`);
        if (node && !isbot(navigator.userAgent)) {
            node.scrollIntoView({behavior: 'smooth', block: 'center'});
        }
    }, []);

    const {ref} = useIntersectionObserver({
        onChange: (isIntersecting) => {
            if (
                isLazyLoadingDisabled ||
                isFetching ||
                pagination.totalCount <= pagination.pageNumber * CATALOG_PAGE_SIZE
            ) {
                return;
            }
            if (isIntersecting && !isbot(navigator.userAgent)) {
                handleLoadNext();
            }
        },
        initialIsIntersecting: false,
    });

    const LAZY_ITEMS: IProductShort[] = Array(CATALOG_PAGE_SIZE)
        .fill(PRODUCT_PLACEHOLDER)
        .map((item, idx) => ({...item, offerId: idx}));

    const renderProductSnippet = useCallback(
        (product: IProductShort, position: number, self: IProductShort[]) => (
            <React.Fragment key={product.offerId}>
                <ProductSnippet
                    onClick={() => {
                        sessionStorage.setItem('productScrollId', product.offerId.toString());
                    }}
                    ref={!isLazyLoadingDisabled && position === self.length - 4 ? ref : null}
                    {...product}
                    url={isSearchListing ? withRedirectParam(product.url) : product.url}
                    className={style.productListItem}
                    listType={view}
                    analytics={{positionIndex: position + 1}}
                />
            </React.Fragment>
        ),
        [isLazyLoadingDisabled, isSearchListing, ref, view],
    );

    const renderProductList = useMemo(() => list.map(renderProductSnippet), [list, renderProductSnippet]);

    const renderPlaceholders = useMemo(
        () =>
            LAZY_ITEMS.map((product) => {
                return (
                    <React.Fragment key={product.offerId}>
                        <ProductSnippet {...product} className={style.productListItem} listType={view} />
                    </React.Fragment>
                );
            }),
        [LAZY_ITEMS, view],
    );

    const renderBanner = useCallback(
        (index: number) => (
            <ProductBannerCard
                data-skeleton-item={true}
                {...meta?.banner}
                id={meta?.banner?.id ?? ''}
                link={meta?.banner?.link ?? ''}
                image={getMediaSrc(meta?.banner?.image || '')}
                title={meta?.banner?.title ?? ''}
                view={view}
                analytics={{
                    positionIndex: index,
                }}
            />
        ),
        [meta?.banner, view],
    );

    // TODO: вынести в отдельный хендлер
    const insertBanners = useCallback(
        (items: JSX.Element[], banner?: ICatalogMeta['banner']) => {
            if (!banner) {
                return items;
            }

            const productList = [];
            const itemsCount = items.length;
            let currentIdx = 0;
            let bannerListIdx = BANNER_LIST_INDEX;
            while (currentIdx < itemsCount) {
                const item = items[currentIdx];
                if (currentIdx === bannerListIdx) {
                    productList.push(
                        <React.Fragment key={`banner-${currentIdx}`}>{renderBanner(currentIdx + 1)}</React.Fragment>,
                    );
                    productList.push(item);
                    bannerListIdx += CATALOG_PAGE_SIZE - 1;
                } else {
                    productList.push(item);
                }
                currentIdx += 1;
            }

            return productList;
        },
        [renderBanner],
    );

    const listWithBannerByView = useMemo(() => {
        const listByView = {
            list:
                renderProductList.length <= BANNER_LIST_INDEX
                    ? renderProductList
                    : insertBanners(renderProductList, meta?.banner),
            tile:
                renderProductList.length <= BANNER_LIST_INDEX
                    ? renderProductList
                    : insertBanners(renderProductList, meta?.banner),
        };

        return listByView[view];
    }, [insertBanners, view, meta?.banner, renderProductList]);

    const flocktoryCategoryId = useRef(list[0]?.sections[0]?.id);

    if (!list.length && !isLoading) {
        return <EmptyProductList />;
    }

    const productsByPage = meta?.banner ? CATALOG_PAGE_SIZE - 1 : CATALOG_PAGE_SIZE;

    return (
        <>
            {hasMoreProductsOnTop && (
                <Button
                    className={style.loadPrevButton}
                    leftIcon={<Icon icon={'history'} />}
                    onClick={handleLoadPrev}
                    theme={'ghost'}
                    isWithoutPaddings={true}
                >
                    Показать предыдущие товары
                </Button>
            )}
            <div
                className={classNames(style.productList, className, {
                    [style[view]]: Boolean(view),
                    [style.productListWithFilter]: isFilterPanelOpen,
                })}
            >
                {isLoadingPrev && (
                    <SkeletonWrapper className={classNames(style.placeholder)} disabled={false}>
                        {renderPlaceholders}
                    </SkeletonWrapper>
                )}
                {isLoading ? (
                    <SkeletonWrapper className={classNames(style.placeholder)} disabled={!isLoading}>
                        {renderPlaceholders}
                    </SkeletonWrapper>
                ) : (
                    listWithBannerByView
                )}
                {isLoadingNext && (
                    <SkeletonWrapper className={classNames(style.placeholder)} disabled={false}>
                        {renderPlaceholders}
                    </SkeletonWrapper>
                )}
            </div>

            {productsByPage < pagination.totalCount && (
                <ListPagination
                    className={style.pagination}
                    isLoading={isLoading}
                    onChange={handleSetPage}
                    pageNumber={pagination.pageNumber}
                    pageSize={CATALOG_PAGE_SIZE}
                    recordCount={pagination.totalCount}
                    scrollOnShowMore={'STOCK_X' === pageType}
                />
            )}

            <Modal hasPortal={true} name={MODALS.PRODUCT_AVAILABILITY_INFO.name} willMount={true}>
                <AvailabilityInfoModal />
            </Modal>
            <FlocktoryCategoryView categoryId={flocktoryCategoryId.current} />
        </>
    );
};
